import { Link, useSearchParams } from "react-router-dom";
import { clsx } from "clsx";
import { useAuth } from "../../lib/auth.js";
import { useFeatures } from "../../hooks/useFeatures.js";
import { Spinner } from "../../components/ui.js";
import { MotdDocument } from "./MotdDocument.js";
import "./motd-prose.css";
import { motdChannelForEnv } from "./channel.js";
import type { MotdChannel } from "./types.js";
import { useMotdEntry, useMotdIndex } from "./useMotd.js";

function parseChannelParam(raw: string | null, fallback: MotdChannel | null): MotdChannel | null {
  if (raw === "staging" || raw === "dev") return raw;
  return fallback;
}

/** Minimal datestamped MOTD archive — public route, no CMS. */
export function MotdArchive(): JSX.Element {
  const { user } = useAuth();
  const { deployEnv, isLoading: featuresLoading } = useFeatures();
  const [params, setParams] = useSearchParams();
  const envChannel = motdChannelForEnv(deployEnv);
  const isAdmin = Boolean(user?.roles.includes("Admin"));
  const showChannelTabs = isAdmin && envChannel === "staging";
  const channel = parseChannelParam(params.get("channel"), envChannel ?? "dev");
  const selectedId = params.get("id");

  const { data: index, isLoading: indexLoading } = useMotdIndex(channel);
  const { data: entry, isLoading: entryLoading } = useMotdEntry(
    channel,
    selectedId ?? index?.entries[0]?.id ?? null,
  );

  const loading = featuresLoading || indexLoading || (selectedId != null && entryLoading);

  const setChannel = (nextChannel: MotdChannel): void => {
    const next = new URLSearchParams(params);
    next.set("channel", nextChannel);
    next.delete("id");
    setParams(next);
  };

  return (
    <div className="mx-auto min-h-screen max-w-5xl px-4 py-8 sm:px-6">
      <p className="text-sm text-slate-500">
        {user ? (
          <Link to="/" className="text-brand underline-offset-2 hover:underline">
            ← Back to app
          </Link>
        ) : (
          <Link to="/login" className="text-brand underline-offset-2 hover:underline">
            ← Back to sign in
          </Link>
        )}
      </p>
      <h1 className="mt-4 text-xl font-bold text-slate-900">Message archive</h1>

      {showChannelTabs ? (
        <div className="mt-3 flex max-w-xs items-center gap-1 rounded-md bg-slate-100 p-1">
          {(
            [
              ["staging", "Staging"],
              ["dev", "Dev"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={clsx(
                "flex-1 rounded px-2 py-1.5 text-sm font-medium",
                channel === value
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-slate-600 hover:text-slate-900",
              )}
              onClick={() => setChannel(value)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : (
        <p className="mt-1 text-sm text-slate-600">
          Channel: <span className="font-medium">{channel ?? "—"}</span>
          {envChannel && envChannel !== channel ? (
            <button
              type="button"
              className="ml-2 text-brand underline-offset-2 hover:underline"
              onClick={() => setChannel(envChannel)}
            >
              Use this host’s channel ({envChannel})
            </button>
          ) : null}
        </p>
      )}

      {loading ? (
        <div className="mt-8">
          <Spinner label="Loading archive…" />
        </div>
      ) : !index?.entries.length ? (
        <p className="mt-8 text-sm text-slate-500">No archived messages for this channel yet.</p>
      ) : (
        <div className="mt-8 grid gap-8 lg:grid-cols-[14rem_1fr]">
          <nav aria-label="Archive dates" className="space-y-1 text-sm">
            {index.entries.map((e) => {
              const active = (selectedId ?? index.entries[0]?.id) === e.id;
              return (
                <button
                  key={e.id}
                  type="button"
                  className={`block w-full rounded px-2 py-1.5 text-left ${
                    active ? "bg-slate-100 font-medium text-slate-900" : "text-slate-600 hover:bg-slate-50"
                  }`}
                  onClick={() => {
                    const next = new URLSearchParams(params);
                    if (channel) next.set("channel", channel);
                    next.set("id", e.id);
                    setParams(next);
                  }}
                >
                  <span className="tabular-nums">{e.date}</span>
                  <span className="mt-0.5 block truncate text-xs text-slate-500">{e.headline}</span>
                </button>
              );
            })}
          </nav>
          {entry ? (
            <MotdDocument
              entry={entry}
              className="w-full max-w-3xl"
              footer={<span className="tabular-nums">{entry.date}</span>}
            />
          ) : (
            <p className="text-sm text-slate-500">Select a date.</p>
          )}
        </div>
      )}
    </div>
  );
}
